<script setup lang="ts">
import { NuxtLink } from '#components';

interface Props {
  url?: string | null;
  target?: string;
  disabled?: boolean;
  loading?: boolean;
  secondary?: boolean;
  clean?: boolean;
  xs?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

interface Slots {
  default?: any;
}

const props = withDefaults(defineProps<Props>(), { url: null, target: '_self', type: 'button' });
const slots = defineSlots<Slots>();
const isHovering = ref(false);
const isActive = ref(false);

const tag = computed<string | Component>(() => (props.url ? NuxtLink : 'button'));

const classes = computed(() => ({
  'base-button_disabled': props.disabled,
  'base-button_loading': props.loading,
  'base-button_secondary': props.secondary,
  'base-button_hover': isHovering.value,
  'base-button_xs': props.xs,
  'base-button_active': isActive.value,
  'base-button_clean': props.clean,
}));
</script>

<template>
  <component
    :is="tag"
    class="base-button"
    :href="url"
    :target="target"
    :class="classes"
    :type="type"
    :disabled="disabled || loading"
    @mouseenter="isHovering = true"
    @mouseleave="isHovering = false; isActive = false;"
    @mousedown="isActive = true"
    @mouseup="isActive = false"
  >
    <slot></slot>
  </component>
</template>

<style scoped lang="scss">
.base-button {
  $block: &;

  --base-button-height: 56px;
  --base-button-padding: 0 24px;
  --base-button-bg-color: #{$color-neutral-100};
  --base-button-border-color: transparent;
  --base-button-color: #{$color-neutral-0};

  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  will-change: color, transform;
  border-style: solid;
  border-radius: 4px;
  border-width: 1px;

  padding: var(--base-button-padding);
  color: var(--base-button-color);
  background: var(--base-button-bg-color);
  height: var(--base-button-height);
  border-color: var(--base-button-border-color);

  &_xs {
    --base-button-height: 40px;
    --base-button-padding: 0 12px;
  }

  &_hover {
    --base-button-bg-color: #{$color-accent-1-hover};
  }

  &_active {
    transform: scale(0.98);
  }

  &_disabled {
    --base-button-bg-color: #{$color-neutral-25};
    @include disableElement();
    cursor: default;
    pointer-events: auto;
  }

  &_secondary {
    --base-button-color: #{$color-neutral-100};
    --base-button-bg-color: transparent;
    --base-button-border-color: #{$color-neutral-50};

    &#{$block}_hover {
      --base-button-border-color: #{$color-neutral-100};
    }

    &#{$block}_disabled {
      --base-button-bg-color: #{$color-neutral-10};
      --base-button-color: #{$color-neutral-25};
      --base-button-border-color: #{$color-neutral-25};
    }
  }

  &_clean {
    --base-button-bg-color: transparent;
    --base-button-border-color: transparent;
    --base-button-padding: 0;
    border: none;
    height: auto;
  }

  &_loading {
    pointer-events: none;
  }
}
</style>
